// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Stack, Grid, Box } from "@mui/material";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from "react-chartjs-2";
import { Oval } from 'react-loader-spinner';
import { apiRequest } from "../../services/utils/HttpUtils";
import { useQuery } from "@tanstack/react-query";
import { getUserInfo } from "../../graphql/queries_user_info";
import totaliser from "./totaliser";
import { RetrieveStateSA4SuperSectorInvestment } from "../../graphql/queries_nationalMap";

export default function InvestmentChart({ queryVariables, setInvestmentTab, updateInvestmentCharts, updateAlert }) {

    // Authentication
    const { instance, accounts } = useMsal();
    const { data: userInfo } = getUserInfo();

    // Chart component references
    const investmentChartLoader = useRef();

    // Chart colours
    const state_colours = [{ "state": "ACT", "colour": "#7e2d40" },
    { "state": "NSW", "colour": "#253746" },
    { "state": "NT", "colour": "#73b469" },
    { "state": "QLD", "colour": "#e7b78a" },
    { "state": "SA", "colour": "#ece81a" },
    { "state": "TAS", "colour": "#ce6c45" },
    { "state": "VIC", "colour": "#8c4799" },
    { "state": "WA", "colour": "#abc7ca" }];

    const supersector_colours = [{ "supersector": "Buildings", "colour": "#e15759" },
    { "supersector": "Transport", "colour": "#f28e2b" },
    { "supersector": "Utilities", "colour": "#4e79a7" },
    { "supersector": "Resources", "colour": "#C98BB9" }];

    // Chart data references
    const stateInvestmentValuesRef = useRef<any[]>([]);
    const sa4InvestmentValuesRef = useRef<any[]>([]);
    const superSectorInvestmentValuesRef = useRef<any[]>([]);

    const { data: stateSa4SuperSectorInvestmentValues, status: stateSa4SuperSectorInvestmentStatus, fetchStatus: stateSa4SuperSectorInvestmentFetchStatus, error: stateSa4SuperSectorInvestmentError, refetch: refetchStateSa4SuperSectorInvestmentValues } = useQuery({
        queryKey: ["StateSA4SuperSectorInvestmentValues"],
        queryFn: () => {
            return apiRequest(
                {
                    query: RetrieveStateSA4SuperSectorInvestment,
                    variables: queryVariables,
                    instance: instance,
                    accounts: accounts
                })
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        cacheTime: Infinity,
        enabled: !!userInfo
    });

    const formatValue = (value) => {
        return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", currencyDisplay: "narrowSymbol", notation: "compact", compactDisplay: "short" }).format(value);
    };

    // Start - State investment chart
    // Set the state chart options
    const state_options = {
        layout: {
            padding: {
                top: 20
            }
        },
        plugins: {
            title: {
                display: false,
                text: 'Total investment per state ($)'
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        let label = tooltipItem.dataset.label;
                        return label + ': ' + formatValue(tooltipItem.parsed.y);
                    }
                }
            },
            legend: {
                position: "bottom",
                onClick: (e, legendItem, legend) => {
                    let index = legendItem.datasetIndex;
                    let ci = legend.chart;
                    var alreadyHidden = (ci.getDatasetMeta(index).hidden === null) ? false : ci.getDatasetMeta(index).hidden;
                    ci.data.datasets.forEach((e, i) => {
                        var meta = ci.getDatasetMeta(i);
                        if (i !== index) {
                            if (!alreadyHidden) {
                                meta.hidden = meta.hidden === null ? !meta.hidden : null;
                            } else if (meta.hidden === null) {
                                meta.hidden = true;
                            }
                        } else if (i === index) {
                            meta.hidden = null;
                        }
                    });
                    ci.update();
                }
            },
            datalabels: {
                anchor: 'end',
                align: 'end',
                offset: 0,
                formatter: (value, ctx) => {
                    const total = ctx.chart.$totalizer.totals[ctx.dataIndex]
                    return formatValue(total);
                },
                display: function (ctx) {
                    return ctx.datasetIndex === ctx.chart.$totalizer.utmost
                }
            }
        },
        responsive: false,
        aspectRatio: 1,
        scales: {
            x: {
                stacked: true
            },
            y: {
                stacked: true,
                ticks: {
                    callback: (value, index, ticks) => {
                        return formatValue(value);
                    }
                }
            }
        }
    };

    // Build the chart datasets dynamically
    const states = [...new Set(stateInvestmentValuesRef.current.map(item => item.State))];
    const years = [...new Set(stateInvestmentValuesRef.current.map(item => item.Year))];
    // Not sure if there is a cleaner way to
    // write the following code. Essentially it
    // is adding missing Year/ResourceGroup data.
    // The chart will fail to render correctly if
    // the missing data is not there.
    // Note: this code solution only applies to 
    // stacked bar chart.
    years.forEach(year => {
        states.forEach(state => {
            const exists = stateInvestmentValuesRef.current.find(i => i.Year === year && i.State === state);
            if (!exists) {
                stateInvestmentValuesRef.current.push({
                    State: state,
                    Year: year,
                    Value: 0
                });
            }
        });
    });

    // Sort the results from the above
    stateInvestmentValuesRef.current.sort((a, b) => {
        return a.Year - b.Year || a.State.localeCompare(b.State);
    });

    // Build the datasets
    const state_datasets = [...new Set(states.map(state => {
        return {
            label: state,
            data: stateInvestmentValuesRef.current.filter((obj) => obj.State === state).map((filObj) => filObj.Value),
            backgroundColor: state_colours.find(x => x.state === state)?.colour
        }
    }))];

    // Sort the data by the investment amount
    // and create the unique years array
    state_datasets.sort((a, b) => Math.max(...b.data) - Math.max(...a.data));
    const state_chart = {
        labels: years,
        datasets: state_datasets
    };
    // End - State investment chart

    // Start - SA4 investment chart
    const sa4_options = {
        indexAxis: 'y',
        layout: {
            padding: {
                right: 20
            }
        },
        plugins: {
            title: {
                display: false,
                text: 'Total investment per SA4 region ($)',
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        return formatValue(tooltipItem.parsed.x);
                    }
                }
            },
            legend: {
                display: false
            }
        },
        responsive: false,
        aspectRatio: 1,
        scales: {
            x: {
                position: "top",
                ticks: {
                    callback: (value, index, ticks) => {
                        return formatValue(value);
                    }
                }
            },
            y: {
                stacked: true
            }
        }
    };

    // Build the chart properties dynamically
    sa4InvestmentValuesRef.current.sort((a, b) => a.State.localeCompare(b.State) || b.Value - a.Value);
    const total = sa4InvestmentValuesRef.current.reduce((n: number, { Value }) => n + Value, 0)
    const sa4_proportions = [...new Set(sa4InvestmentValuesRef.current.map(item => item.Value / total))];
    const sa4_regions = [...new Set(sa4InvestmentValuesRef.current.map(item => item.LocationType))];
    const sa4_values = [...new Set(sa4InvestmentValuesRef.current.map(item => item.Value))];
    const sa4_colours = sa4InvestmentValuesRef.current.map(item => state_colours.find(x => x.state === item.State)?.colour);

    // Set the chart labels and dataset
    const sa4_chart = {
        labels: sa4_regions,
        proportions: sa4_proportions,
        datasets: [{
            data: sa4_values,
            backgroundColor: sa4_colours,
            datalabels: {
                anchor: 'end',
                align: 'end',
                offset: 0,
                formatter: function (value, context) {
                    return new Intl.NumberFormat("en-AU", { style: "percent", maximumFractionDigits: 2 }).format(sa4_proportions[context.dataIndex]);
                }
            }
        }]
    };
    // End - SA4 investment chart

    // Start - Super sector investment chart
    // Set the super sector chart options
    const supersector_options = {
        layout: {
            padding: {
                top: 20
            }
        },
        plugins: {
            title: {
                display: false,
                text: 'Total investment per sector ($)'
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        let label = tooltipItem.dataset.label;
                        return label + ': ' + formatValue(tooltipItem.parsed.y);
                    }
                }
            },
            legend: {
                position: "bottom",
                onClick: (e, legendItem, legend) => {
                    let index = legendItem.datasetIndex;
                    let ci = legend.chart;
                    var alreadyHidden = (ci.getDatasetMeta(index).hidden === null) ? false : ci.getDatasetMeta(index).hidden;
                    ci.data.datasets.forEach((e, i) => {
                        var meta = ci.getDatasetMeta(i);
                        if (i !== index) {
                            if (!alreadyHidden) {
                                meta.hidden = meta.hidden === null ? !meta.hidden : null;
                            } else if (meta.hidden === null) {
                                meta.hidden = true;
                            }
                        } else if (i === index) {
                            meta.hidden = null;
                        }
                    });
                    ci.update();
                }
            },
            datalabels: {
                anchor: 'end',
                align: 'end',
                offset: 0,
                formatter: (value, ctx) => {
                    const total = ctx.chart.$totalizer.totals[ctx.dataIndex]
                    return formatValue(total);
                },
                display: function (ctx) {
                    return ctx.datasetIndex === ctx.chart.$totalizer.utmost
                }
            }
        },
        responsive: false,
        aspectRatio: 1,
        scales: {
            x: {
                stacked: true
            },
            y: {
                stacked: true,
                ticks: {
                    callback: (value, index, ticks) => {
                        return formatValue(value);
                    }
                }
            }
        }
    };

    // Build the chart datasets dynamically
    const supersectors = [...new Set(superSectorInvestmentValuesRef.current.map(item => item.SuperSector))];
    const supersector_years = [...new Set(superSectorInvestmentValuesRef.current.map(item => item.Year))];
    // Not sure if there is a cleaner way to
    // write the following code. Essentially it
    // is adding missing Year/ResourceGroup data.
    // The chart will fail to render correctly if
    // the missing data is not there.
    // Note: this code solution only applies to 
    // stacked bar chart.
    supersector_years.forEach(year => {
        supersectors.forEach(supersector => {
            const exists = superSectorInvestmentValuesRef.current.find(i => i.Year === year && i.SuperSector === supersector);
            if (!exists) {
                superSectorInvestmentValuesRef.current.push({
                    SuperSector: supersector,
                    Year: year,
                    Value: 0
                });
            }
        });
    });

    // Sort the results from the above
    superSectorInvestmentValuesRef.current.sort((a, b) => {
        return a.Year - b.Year || a.SuperSector.localeCompare(b.SuperSector);
    });

    // Build the datasets
    const supersector_datasets = [...new Set(supersectors.map(supersector => {
        return {
            label: supersector,
            data: superSectorInvestmentValuesRef.current.filter((obj) => obj.SuperSector === supersector).map((filObj) => filObj.Value),
            backgroundColor: supersector_colours.find(x => x.supersector === supersector)?.colour
        }
    }))];

    // Sort the data by the investment amount
    // and create the unique years array
    supersector_datasets.sort((a, b) => Math.max(...b.data) - Math.max(...a.data));
    const supersector_chart = {
        labels: supersector_years,
        datasets: supersector_datasets
    };
    // End - State investment chart

    useEffect(() => {
        if (stateSa4SuperSectorInvestmentStatus == 'error') {
            console.log(stateSa4SuperSectorInvestmentError);
        } else if (stateSa4SuperSectorInvestmentStatus == 'success' && stateSa4SuperSectorInvestmentFetchStatus == 'idle') {
            const records = stateSa4SuperSectorInvestmentValues.executeRetrieveStateSA4SuperSectorInvestment;

            stateInvestmentValuesRef.current = [];
            records.reduce((res, value) => {
                if (!res[value.State + '-' + value.Year]) {
                    res[value.State + '-' + value.Year] = { State: value.State, Value: 0, Year: value.Year };
                    stateInvestmentValuesRef.current.push(res[value.State + '-' + value.Year])
                }
                res[value.State + '-' + value.Year].Value += value.Value;
                return res;
            }, {});

            sa4InvestmentValuesRef.current = [];
            records.reduce((res, value) => {
                if (value.LocationType !== 'Unknown') {
                    if (!res[value.LocationType]) {
                        res[value.LocationType] = { State: value.State, LocationType: value.LocationType, Value: 0 };
                        sa4InvestmentValuesRef.current.push(res[value.LocationType])
                    }
                    res[value.LocationType].Value += value.Value;
                }
                return res;
            }, {});

            superSectorInvestmentValuesRef.current = [];
            records.reduce((res, value) => {
                if (!res[value.SuperSector + '-' + value.Year]) {
                    res[value.SuperSector + '-' + value.Year] = { SuperSector: value.SuperSector, Value: 0, Year: value.Year };
                    superSectorInvestmentValuesRef.current.push(res[value.SuperSector + '-' + value.Year])
                }
                res[value.SuperSector + '-' + value.Year].Value += value.Value;
                return res;
            }, {});

            investmentChartLoader.current!.style.display = 'none';
            setInvestmentTab(false);
        }
    }, [stateSa4SuperSectorInvestmentValues, stateSa4SuperSectorInvestmentStatus, stateSa4SuperSectorInvestmentFetchStatus, stateSa4SuperSectorInvestmentError]);

    useEffect(() => {
        if (updateInvestmentCharts) {
            investmentChartLoader.current!.style.display = 'flex';
            refetchStateSa4SuperSectorInvestmentValues();
        }
    }, [updateInvestmentCharts]);

    return (<Stack>
        <Box ref={investmentChartLoader} style={{ position: "absolute", display: "flex", zIndex: "200", width: 1000, height: 1800, justifyContent: "center", alignItems: "center", backgroundColor: "rgba(51, 51, 51, 0.4)" }}>
            <Oval
                visible={true}
                height="80"
                width="80"
                color="#005670"
                secondaryColor="#005670"
                ariaLabel="oval-loading"
                wrapperStyle={{}}
                wrapperClass=""
            />
        </Box>
        <Grid container direction="column">
            <Grid item>
                <Box style={{ display: "flex", width: 1000, height: 48, paddingBottom: 2, backgroundColor: "rgb(0, 86, 112)", color: "#FFF", fontWeight: 600, fontSize: "1.25rem", fontFamily: "ProximaNova", justifyContent: "flex-start", alignItems: "center" }}>Total investment per state ($)
                </Box>
                <Box style={{ position: "relative", width: 1000, height: 550 }}>
                    <Bar options={state_options} data={state_chart} plugins={[ChartDataLabels, totaliser]} width="1000" height="550" />
                </Box>
            </Grid>
            <Grid item>
                <Box style={{ display: "flex", width: 1000,  height: 48, padding: 2, backgroundColor: "rgb(0, 86, 112)", color: "#FFF", fontWeight: 600, fontSize: "1.25rem", fontFamily: "ProximaNova", justifyContent: "flex-start", alignItems: "center" }}>Total investment per SA4 region ($)
                </Box>
                <Box style={{ position: "relative", width: 1000, height: 550, overflow: "auto" }}>
                    <Bar options={sa4_options} data={sa4_chart} plugins={[ChartDataLabels]} height="1000" width="980" />
                </Box>
            </Grid>
            <Grid item>
                <Box style={{ display: "flex", width: 1000,  height: 48, padding: 2, backgroundColor: "rgb(0, 86, 112)", color: "#FFF", fontWeight: 600, fontSize: "1.25rem", fontFamily: "ProximaNova", justifyContent: "flex-start", alignItems: "center" }}>Total investment per sector ($)
                </Box>
                <Box style={{ position: "relative", width: 1000, height: 550 }}>
                    <Bar options={supersector_options} data={supersector_chart} plugins={[ChartDataLabels, totaliser]} width="1000" height="550" />
                </Box>
            </Grid>
        </Grid>
    </Stack>)
}